/**
 * WhatsApp helpers — Prompt 8.
 * Two numbers are now tracked in settings:
 *   - websiteWhatsapp(WaMe) → used by EVERY reservation/contact button
 *   - socialWhatsapp(WaMe)  → reference only, never opened from the website
 *
 * The user can override either number from /admin/settings. Legacy keys
 * (contactPhone, reservationWhatsapp) are kept as final fallbacks.
 */
import { trackWhatsAppClick, buildOriginBlock, getTrackingContext } from "@/lib/tracking";

// Hard-coded defaults — match the values seeded by `migrate_unify_phone()`
// in backend/server.py so first paint never sees a placeholder old number.
export const WEBSITE_PHONE_DEFAULT = "+34 613 207 817";
export const WEBSITE_WA_DEFAULT = "34613207817";

/** Canonical wa.me digits for the website (NOT social). */
export function waNumber(settings) {
  const raw =
    settings?.websiteWhatsappWaMe ||
    settings?.websiteWhatsapp ||
    settings?.websitePhone ||
    settings?.reservationWhatsapp ||
    settings?.contactPhone ||
    WEBSITE_WA_DEFAULT;
  return String(raw).replace(/[^0-9]/g, "") || WEBSITE_WA_DEFAULT;
}

/** Human phone for visible labels. */
export function websitePhoneLabel(settings) {
  return (
    settings?.websitePhone ||
    settings?.websiteWhatsapp ||
    settings?.reservationWhatsapp ||
    settings?.contactPhone ||
    WEBSITE_PHONE_DEFAULT
  );
}

function appendOrigin(message, originLabels) {
  // originLabels = { origen, campaign, referencia } already translated.
  const { headers, channel, campaign, ref } = originLabels;
  const block =
    `\n\n${headers.origen}: ${channel}` +
    `\n${headers.campaign}: ${campaign}` +
    `\n${headers.ref}: ${ref}`;
  return (message || "") + block;
}

/** Labels per language for the Origin block — Prompt 8 §9. */
const ORIGIN_HEADERS = {
  en: { origen: "Source", campaign: "Campaign", ref: "Reference" },
  es: { origen: "Origen", campaign: "Campaña", ref: "Referencia" },
  ru: { origen: "Источник", campaign: "Кампания", ref: "Референс" },
};

/**
 * Build the final WhatsApp URL with the Origin block appended.
 *
 * `meta` is the click context — `{ vehicle, language, pickupDate, returnDate }`.
 */
export function buildWhatsAppUrl(message, settings, lang = "en") {
  const num = waNumber(settings);
  const ctx = getTrackingContext();
  const origin = buildOriginBlock(ctx);
  const headers = ORIGIN_HEADERS[lang] || ORIGIN_HEADERS.en;
  const full = appendOrigin(message, { headers, ...origin });
  return `https://wa.me/${num}?text=${encodeURIComponent(full)}`;
}

/**
 * Open WhatsApp, append Origin block, and asynchronously record the event.
 * `meta` carries vehicle/language/date info for tracking; it is OPTIONAL —
 * pages that only have a generic CTA can pass nothing.
 */
export function openWhatsApp(message, settings, lang = "en", meta = {}) {
  if (typeof window === "undefined") return;
  const num = waNumber(settings);

  trackWhatsAppClick({
    vehicleId: meta.vehicleId || meta.vehicle?.id || "",
    vehicleName: meta.vehicleName || meta.vehicle?.name || "",
    language: lang,
    pickupDate: meta.pickupDate || "",
    returnDate: meta.returnDate || "",
    destinationWhatsapp: websitePhoneLabel(settings),
    destinationWaMe: num,
  });

  const url = buildWhatsAppUrl(message, settings, lang);
  window.open(url, "_blank", "noopener");
}
